import {useQuery} from "@tanstack/react-query";
import {getUserPaymentCard} from "api";
import {cardTypeLogos} from "conts";
import {useToast} from "hooks";
import {userRoute} from "pages/user/user.routes";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import {ButtonPrimary} from "ui/base/Button/ButtonStyled";
import {RadioButton} from "ui/base/radioButton/RadioButton";
import {hideScrollBar} from "ui/base/scrollbarStyle/scrollbarStyle";
import {Tooltip} from "ui/base/tooltip/Tooltip";
import {themeColor, themeFontSize} from "ui/theme";
import {CancellationPolicyContent} from "../components/CancellationPolicyContent";
import {PaymentStripeForm} from "../components/PaymentStripeForm";
import {bookingErrorMessage, buildBookingPayload, useBookingRequest} from "../hooks/useBookingRequest";
import {ThankYouModal} from "../components/ThankYouModal";

const defaultCardOption = {
    value: "new",
    label: "New Payment Method",
    type: "new",
};

const NEW_CARD_VALUE = "new";

const getHiddenCardNumber = (numbers) => `**** **** **** ${numbers}`;

export const PaymentForm = ({
                                lessonsCostData,
                                haveDiscount,
                                timing,
                                class_id,
                                onlineType,
                                subjectToTeach,
                                numberOfStudents,
                                teacher_id,
                                firstName,
                                lastName,
                                cancellation,
                                typeOfClass,
                            }) => {

    const [selectedCard, setSelectedCard] = useState(null);
    const initializedRef = useRef(false);
    const [successfulPayment, setSuccessfulPayment] = useState(false);
    const navigate = useNavigate();


    const {
        data: cardsResp
    } = useQuery({
        queryKey: ["getUserPaymentCard"],
        queryFn: getUserPaymentCard,
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    const cardOptions = useMemo(() => {
        const data = cardsResp?.data ?? [];
        const formatted = data.map((card) => ({
            label: getHiddenCardNumber(card.card_last_4_digit),
            value: card.stripe_card_id,
            expiration: card.expiry_date,
            type: card.card_type,
            isDefault: card.is_default,
        }));
        return [...formatted, defaultCardOption];
    }, [cardsResp]);


    useEffect(() => {
        if (initializedRef.current) return;

        const defaultOpt = cardOptions.find((c) => c.isDefault);
        const anySaved = cardOptions.find((c) => c.value !== NEW_CARD_VALUE);

        const initial =
            defaultOpt?.value ||
            anySaved?.value ||
            NEW_CARD_VALUE;

        setSelectedCard(initial);
        initializedRef.current = true;
    }, [cardOptions]);

    useEffect(() => {
        if (!selectedCard) return;
        const stillExists = cardOptions.some((c) => c.value === selectedCard);
        if (stillExists) return;

        const fallbackDefault = cardOptions.find((c) => c.isDefault)?.value;
        const fallbackAnySaved = cardOptions.find((c) => c.value !== NEW_CARD_VALUE)?.value;

        setSelectedCard(fallbackDefault ?? fallbackAnySaved ?? NEW_CARD_VALUE);
    }, [cardOptions, selectedCard]);

    const {createCardPaymentMethod, requestBooking, isSubmitting} = useBookingRequest();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedCard || isSubmitting) return;

        const payload = buildBookingPayload({
            timing,
            lessonsCost: lessonsCostData,
            classId: subjectToTeach,
            typeOfClass,
            numberOfStudents,
            teacherId: teacher_id,
            onlineType,
        });

        try {
            const isNewCard = selectedCard === NEW_CARD_VALUE;
            const cardId = isNewCard ? await createCardPaymentMethod() : selectedCard;
            await requestBooking({payload, cardId, isNewCard});
            setSuccessfulPayment(true);
        } catch (error) {
            useToast("error", bookingErrorMessage(error, "Error requesting booking"));
        }
    };

    const isLoading = isSubmitting;

    return (
        <>
            <Container onSubmit={handleSubmit}>
                <RadioGroup>
                    <SectionTitle>Select payment method</SectionTitle>

                    {cardOptions.map((card) => (
                        <StyledRadioButton
                            type="button"
                            key={card.value}
                            selected={selectedCard === card.value}
                            onClick={() => setSelectedCard(card.value)}
                            disabled={isLoading}
                        >
                            {cardTypeLogos?.[card.type]?.img ?? <span>💳</span>}
                            <CardContent>
                                {card.label}
                                {card.expiration ? (
                                    <CardExp>Exp. date {card.expiration}</CardExp>
                                ) : null}
                            </CardContent>
                        </StyledRadioButton>
                    ))}
                </RadioGroup>

                {selectedCard === NEW_CARD_VALUE ? (
                    <FormSection>
                        <SectionTitle>Credit Card Details</SectionTitle>
                        <PaymentStripeForm/>
                    </FormSection>
                ) : null}

                <Footer>
                    <Agreement>
                        By clicking the button below, you agree to the following policies:{" "}
                        <Tooltip
                            id="cancellation-policy-1"
                            text={
                                <CancellationPolicyContent
                                    policy={cancellation}
                                    fullName={firstName + " " + (lastName?.[0] ?? "")}
                                />
                            }
                            display="inline"
                            maxWidth="320px"
                        >
                            <StyledTooltipText>Cancellation policy</StyledTooltipText>
                        </Tooltip>
                        ,{" "}
                        <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://teache.co/policies/terms"
                        >
                            Terms & Conditions
                        </Link>
                    </Agreement>
                    <div>
                        <ButtonPrimary type="submit" disabled={isLoading}>
                            {isLoading ? "Processing…" : "Request To Book"}
                        </ButtonPrimary>
                    </div>
                </Footer>
            </Container>

            {successfulPayment && (
                <ThankYouModal
                    show={successfulPayment}
                    teacherName={firstName}
                    timing={timing}
                    typeOfClass={typeOfClass.label}
                    numberOfStudents={numberOfStudents.label}
                    lessonsCost={lessonsCostData}
                    onSuccess={() => navigate(userRoute.classDetail + class_id)}
                    haveDiscount={haveDiscount}
                />
            )}
        </>
    );
};

const Container = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    min-height: 0;
    border-radius: 5px;
    padding: 1rem;
    max-width: 640px;
    overflow-y: scroll;
    order: 2;
    margin-inline: auto;

    @media (min-width: 1024px) {
        order: 1;
        margin-inline: unset;
    }
    ${hideScrollBar}
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const SectionTitle = styled.h2`
    font-size: ${themeFontSize("regular")};
    font-weight: bold;
    color: ${themeColor("txt")};
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Agreement = styled.p`
    font-size: ${themeFontSize("small")};
    color: ${themeColor("txt")};
`;

const StyledTooltipText = styled.span`
    color: ${themeColor("primary")};
    text-decoration: underline;
    cursor: pointer;
`;

const Link = styled.a`
    color: ${themeColor("primary")};
    text-decoration: underline;
    cursor: pointer;
`;

const RadioGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StyledRadioButton = styled(RadioButton)`
    padding: 8px 16px;
    display: flex;
    width: 100%;
    color: #191d3a;
    justify-content: start;
    gap: 16px;
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    text-align: start;
`;

const CardExp = styled.span`
    opacity: 0.6;
    font-size: 12px;
`;
